import { useState } from 'react';
import { AlertTriangle, Download, Plus } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { mensagemDeErro } from '../api';
import { Vacation360, VacationPeriod, VacationRequestItem, VacationScope, exportarEventosFerias } from '../api/ferias';
import { useAjustarFaltas, useCancelarFerias, useEventosFerias, useVisaoFerias } from '../hooks/ferias';
import {
  BOND_STATUS_LABELS, CRITICALITY_LABELS, INSTALLMENT_STATUS_LABELS, PERIOD_STATUS_LABELS, VACATION_DECISION_LABELS,
  VACATION_ORIGIN_LABELS, VACATION_REQUEST_STATUS_LABELS, VACATION_STEP_LABELS, criticalityTone, formatDate,
} from './format';
import { PessoasModal } from './PessoasModal';
import { SolicitarFeriasModal, SolicitarModo } from './SolicitarFeriasModal';
import { CoberturaCliente } from './CoberturaCliente';

interface Props {
  scope: VacationScope;
  /** RH: pode solicitar por qualquer colaborador, cancelar aprovadas e ajustar faltas. */
  hr?: { canCreate: boolean; canEdit: boolean };
  /** Autosserviço: o próprio colaborador solicita, cancela, reenvia e reprograma. */
  self?: boolean;
}

const EVENT_TYPES = [
  ['periodo_aberto', 'Período aberto'], ['periodo_recalculado', 'Período recalculado'], ['faltas_ajustadas', 'Faltas ajustadas'],
  ['alerta', 'Alerta'], ['solicitacao_registrada', 'Solicitação registrada'], ['solicitacao_reenviada', 'Reenvio'],
  ['aprovacao', 'Aprovação de etapa'], ['reprovacao', 'Reprovação'], ['devolucao', 'Devolução'], ['solicitacao_aprovada', 'Férias aprovadas'],
  ['reprogramacao', 'Reprogramação'], ['cancelamento', 'Cancelamento'], ['inicio_ferias', 'Início'], ['retorno_ferias', 'Retorno'],
  ['inicio_nao_processado', 'Início não processado'],
] as const;

/** Visão 360º das férias (US-GP-014) com linha do tempo (US-GP-013). */
export function FeriasPainel({ scope, hr, self }: Props) {
  const visao = useVisaoFerias(scope);
  const [modal, setModal] = useState<SolicitarModo | null>(null);
  const [faltas, setFaltas] = useState<VacationPeriod | null>(null);
  const [cancelando, setCancelando] = useState<VacationRequestItem | null>(null);

  if (visao.isLoading) return <p className="text-sm text-gray-500">Carregando…</p>;
  if (visao.isError || !visao.data) return <p className="text-sm text-red-600">Não foi possível carregar as férias.</p>;
  const v = visao.data;
  if (!v.eligible) {
    return <EmptyState size="compact" title="Férias não se aplicam" description="Só vínculos CLT têm períodos aquisitivos de férias." />;
  }

  const podeSolicitar = (self || hr?.canCreate) && v.periods.some((p) => p.available > 0 && !['em_aquisicao', 'perdido', 'encerrado'].includes(p.status));
  const s = v.summary!;
  const id = v.identification;

  return (
    <div className="space-y-6">
      <section className="flex flex-wrap items-start justify-between gap-3">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
          <Dado label="Matrícula" value={id.internalRegistration} />
          <Dado label="Empresa" value={id.company} />
          <Dado label="Área" value={id.orgUnit} />
          <Dado label="Cargo" value={id.jobPosition} />
          <Dado label="Gestor" value={id.managerName} />
          <Dado label="Admissão" value={formatDate(id.admissionDate)} />
          <Dado label="Situação" value={id.bondStatus ? BOND_STATUS_LABELS[id.bondStatus as keyof typeof BOND_STATUS_LABELS] ?? id.bondStatus : null} />
          <Dado label="Atualizado em" value={v.lastUpdated ? new Date(v.lastUpdated).toLocaleString('pt-BR') : '—'} />
        </dl>
        {podeSolicitar && (
          <Button size="sm" onClick={() => setModal({ kind: 'nova' })}><Plus className="mr-1 h-4 w-4" /> Solicitar férias</Button>
        )}
      </section>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <Card label="Disponível" value={`${s.available} d`} />
        <Card label="Programado" value={`${s.scheduled} d`} />
        <Card label="Gozado" value={`${s.taken} d`} />
        <Card label="Vendido" value={`${s.sold} d`} />
        <Card label="Em aprovação" value={`${s.pending} d`} />
        <Card label="Vencido" value={`${s.expired} d`} tone={s.expired > 0 ? 'text-red-700' : undefined} />
        <Card
          label={s.onVacation ? 'Em férias' : 'Próximas férias'}
          value={s.onVacation ? 'Agora' : s.nextVacation ? formatDate(s.nextVacation.startDate) : '—'}
        />
      </section>

      {v.alerts.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Alertas e pendências</h3>
          <ul className="space-y-1">
            {v.alerts.map((a, i) => (
              <li key={i} className={`flex items-center gap-2 rounded px-3 py-2 text-sm ${criticalityTone(a.level)}`}>
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="font-medium">{CRITICALITY_LABELS[a.level]}:</span> {a.message}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Períodos aquisitivos e concessivos</h3>
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Aquisitivo</th><th className="px-3 py-2">Concessivo até</th><th className="px-3 py-2">Direito</th>
                <th className="px-3 py-2">Gozados</th><th className="px-3 py-2">Programados</th><th className="px-3 py-2">Vendidos</th>
                <th className="px-3 py-2">Saldo</th><th className="px-3 py-2">Situação</th>{hr?.canEdit && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {v.periods.slice().reverse().map((p) => (
                <tr key={p.id}>
                  <td className="px-3 py-2">{formatDate(p.acquisitionStart)} a {formatDate(p.acquisitionEnd)}</td>
                  <td className="px-3 py-2">
                    {formatDate(p.concessionEnd)}
                    {p.daysToExpire !== null && p.daysToExpire >= 0 && p.unplanned > 0 && (
                      <span className="block text-xs text-gray-500">faltam {p.daysToExpire} dias</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{p.daysEntitled}{p.absences > 0 && <span className="block text-xs text-gray-500">{p.absences} faltas</span>}</td>
                  <td className="px-3 py-2">{p.taken}</td>
                  <td className="px-3 py-2">{p.scheduled}{p.pending > 0 && <span className="block text-xs text-gray-500">+{p.pending} em aprovação</span>}</td>
                  <td className="px-3 py-2">{p.sold}</td>
                  <td className={`px-3 py-2 font-medium ${p.available < 0 ? 'text-red-700' : ''}`}>{p.status === 'em_aquisicao' ? '—' : p.available}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${criticalityTone(p.status === 'vencido' ? 'critico' : p.criticality)}`}>
                      {PERIOD_STATUS_LABELS[p.status]}
                    </span>
                    {p.inconsistent && <span className="ml-1 text-xs text-red-700">inconsistente</span>}
                  </td>
                  {hr?.canEdit && (
                    <td className="px-3 py-2 text-right">
                      {!['perdido', 'encerrado'].includes(p.status) && (
                        <Button size="sm" variant="ghost" onClick={() => setFaltas(p)}>Faltas</Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Solicitações</h3>
        {v.requests.length === 0 ? (
          <EmptyState size="compact" title="Nenhuma solicitação de férias" />
        ) : (
          <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
            {v.requests.map((r) => (
              <Solicitacao key={r.id} r={r} hr={hr} self={self}
                onCancel={() => setCancelando(r)}
                onResubmit={() => setModal({ kind: 'reenviar', request: r })}
                onReprogram={() => setModal({ kind: 'reprogramar', request: r })} />
            ))}
          </ul>
        )}
      </section>

      <LinhaDoTempo scope={scope} visao={v} exportavel={scope.kind === 'rh'} />

      <SolicitarFeriasModal open={modal !== null} onClose={() => setModal(null)} visao={v}
        bondId={hr && !self ? id.bondId ?? null : null} asHr={!!hr && !self} modo={modal ?? { kind: 'nova' }} />
      <FaltasModal periodo={faltas} onClose={() => setFaltas(null)} />
      <CancelarModal request={cancelando} asHr={!!hr && !self} onClose={() => setCancelando(null)} />
    </div>
  );
}

function Dado({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-gray-900">{value || '—'}</dd>
    </div>
  );
}

function Card({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-lg font-semibold ${tone ?? 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

function Solicitacao({ r, hr, self, onCancel, onResubmit, onReprogram }: {
  r: VacationRequestItem; hr?: Props['hr']; self?: boolean; onCancel: () => void; onResubmit: () => void; onReprogram: () => void;
}) {
  const naoComecou = !r.installments.some((i) => i.status === 'em_gozo' || i.status === 'concluida');
  const aberta = r.status === 'aguardando_aprovacao' || r.status === 'devolvida';
  const podeCancelar = (self && aberta) || (!self && hr?.canEdit && (aberta || (r.status === 'aprovada' && naoComecou)));
  const podeReenviar = r.status === 'devolvida' && (self || hr?.canEdit);
  const podeReprogramar = self && r.status === 'aprovada' && naoComecou;
  return (
    <li className="space-y-1 px-3 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-gray-900">
          Nº {r.number} · {VACATION_REQUEST_STATUS_LABELS[r.status]}
          {r.currentStep && <span className="ml-2 font-normal text-gray-600">etapa {VACATION_STEP_LABELS[r.currentStep]} ({r.currentResponsible ?? '—'})</span>}
          {r.replacesRequestId && <span className="ml-2 text-xs text-gray-500">(reprogramação)</span>}
        </p>
        <span className="flex gap-2">
          {podeReenviar && <Button size="sm" variant="secondary" onClick={onResubmit}>Ajustar e reenviar</Button>}
          {podeReprogramar && <Button size="sm" variant="secondary" onClick={onReprogram}>Reprogramar</Button>}
          {podeCancelar && <Button size="sm" variant="ghost" onClick={onCancel}>Cancelar</Button>}
        </span>
      </div>
      <p className="text-gray-700">
        {r.installments.filter((i) => i.status !== 'cancelada' || r.status === 'cancelada' || r.status === 'reprogramada' || r.status === 'rejeitada')
          .map((i) => `${formatDate(i.startDate)} a ${formatDate(i.endDate)} (${i.days} d, ${INSTALLMENT_STATUS_LABELS[i.status].toLowerCase()})`).join(' · ')}
      </p>
      <p className="text-xs text-gray-500">
        Aquisitivo {formatDate(r.period.acquisitionStart)} a {formatDate(r.period.acquisitionEnd)}
        {r.sellDays > 0 && ` · ${r.sellDays} dias vendidos`}{r.advance13th && ' · adiantamento do 13º'}
        {' · '}solicitado por {r.requestedByName ?? '—'} ({r.origin === 'rh' ? 'RH' : 'colaborador'})
      </p>
      {r.approvals.length > 0 && (
        <ul className="text-xs text-gray-600">
          {r.approvals.map((a, i) => (
            <li key={i}>
              {VACATION_STEP_LABELS[a.step]}: {VACATION_DECISION_LABELS[a.decision]} por {a.decidedByName ?? '—'} em {new Date(a.decidedAt).toLocaleString('pt-BR')}
              {a.comment ? ` — ${a.comment}` : ''}
            </li>
          ))}
        </ul>
      )}
      {r.cancelledReason && <p className="text-xs text-gray-500">Cancelamento: {r.cancelledReason}</p>}
      {hr && !self && ['aprovada', 'em_gozo', 'concluida', 'cancelada', 'reprogramada'].includes(r.status) && (
        <CoberturaCliente requestId={r.id} personId={r.userId} canEdit={hr.canEdit && ['aprovada', 'em_gozo'].includes(r.status)} />
      )}
    </li>
  );
}

function LinhaDoTempo({ scope, visao, exportavel }: { scope: VacationScope; visao: Vacation360; exportavel: boolean }) {
  const toast = useToast();
  const [type, setType] = useState('');
  const [origin, setOrigin] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const filtrando = !!(type || origin || from || to);
  const filters = { type: type || undefined, origin: origin || undefined, from: from || undefined, to: to || undefined };
  // A equipe não tem rota própria de eventos: o gestor vê os últimos da Visão 360º.
  const eventos = useEventosFerias(scope, filters, filtrando && scope.kind !== 'team');
  const lista = filtrando && scope.kind !== 'team' ? eventos.data ?? [] : visao.events;

  async function exportar() {
    if (scope.kind !== 'rh') return;
    try {
      await exportarEventosFerias(scope.userId, filters);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível exportar a linha do tempo.'));
    }
  }

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Linha do tempo</h3>
        {exportavel && <Button size="sm" variant="secondary" onClick={exportar}><Download className="mr-1 h-4 w-4" /> Exportar CSV</Button>}
      </div>
      {scope.kind !== 'team' && (
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-4">
          <AutocompleteSelect label="Evento" options={[{ value: '', label: 'Todos' }, ...EVENT_TYPES.map(([value, label]) => ({ value, label }))]}
            value={type} onChange={setType} />
          <AutocompleteSelect label="Origem" options={[{ value: '', label: 'Todas' }, ...Object.entries(VACATION_ORIGIN_LABELS).map(([value, label]) => ({ value, label }))]}
            value={origin} onChange={setOrigin} />
          <TextInput label="De" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextInput label="Até" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      )}
      {lista.length === 0 ? (
        <EmptyState size="compact" title="Nenhum evento" />
      ) : (
        <ol className="space-y-2 border-l-2 border-gray-200 pl-4">
          {lista.map((e) => (
            <li key={e.id} className="text-sm">
              <p className="text-gray-900">
                <span className="font-medium">{e.label}</span>
                {e.requestNumber && <span className="text-gray-500"> · solicitação nº {e.requestNumber}</span>}
              </p>
              <p className="text-xs text-gray-500">
                {new Date(e.createdAt).toLocaleString('pt-BR')} · {e.actorName ?? '—'} · {VACATION_ORIGIN_LABELS[e.origin]}
              </p>
              {e.notes && <p className="text-xs text-gray-600">{e.notes}</p>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function FaltasModal({ periodo, onClose }: { periodo: VacationPeriod | null; onClose: () => void }) {
  const toast = useToast();
  const ajustar = useAjustarFaltas();
  const [absences, setAbsences] = useState('0');
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  if (periodo && aberto !== periodo.id) {
    setAberto(periodo.id);
    setAbsences(String(periodo.absences));
    setJustification('');
    setError(null);
  }
  if (!periodo && aberto) setAberto(null);

  async function submit() {
    if (!periodo) return;
    const n = Number(absences);
    if (!Number.isInteger(n) || n < 0) return setError('Informe um número de faltas válido.');
    if (!justification.trim()) return setError('Informe a justificativa.');
    try {
      const r = await ajustar.mutateAsync({ periodId: periodo.id, absences: n, justification: justification.trim() });
      toast.success(`Faltas ajustadas: ${r.daysEntitled} dias de direito.`);
      onClose();
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível ajustar as faltas.'));
    }
  }

  return (
    <PessoasModal open={periodo !== null} title="Faltas injustificadas do período"
      description="Até 5 faltas: 30 dias; 6 a 14: 24; 15 a 23: 18; 24 a 32: 12; acima de 32: sem direito (art. 130 da CLT)."
      submitLabel="Salvar" isSubmitting={ajustar.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <TextInput label="Faltas" type="number" min={0} value={absences} onChange={(e) => setAbsences(e.target.value)} />
      <Textarea label="Justificativa" value={justification} onChange={(e) => setJustification(e.target.value)} />
    </PessoasModal>
  );
}

function CancelarModal({ request, asHr, onClose }: { request: VacationRequestItem | null; asHr: boolean; onClose: () => void }) {
  const toast = useToast();
  const cancelar = useCancelarFerias(asHr);
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!request) return;
    if (asHr && !justification.trim()) return setError('Informe a justificativa.');
    try {
      await cancelar.mutateAsync({ id: request.id, justification: justification.trim() || undefined });
      toast.success('Solicitação cancelada.');
      setJustification('');
      onClose();
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível cancelar.'));
    }
  }

  return (
    <PessoasModal open={request !== null} title={`Cancelar solicitação nº ${request?.number ?? ''}`} submitLabel="Cancelar solicitação"
      submitVariant="danger" isSubmitting={cancelar.isPending} submitError={error} onClose={() => { setError(null); onClose(); }} onSubmit={submit}>
      <Textarea label={asHr ? 'Justificativa' : 'Motivo (opcional)'} value={justification} onChange={(e) => setJustification(e.target.value)} />
    </PessoasModal>
  );
}
